import React from "react";
import { useTranslation } from "react-i18next";
import { ReportFormData } from "../../types/ReportFormData";
import { ScreenGroup } from "../../types/ScreenGroup";
import { Monitor } from "../../types/Screen";
import { getAvailableScreens } from "../../utils/screenGroupHelpers";
import Input from "../ui/Input";
import Button from "../ui/Button";
import SearchableSelect from "./SearchableSelect";

interface RenameScreensSectionProps {
	formData: ReportFormData;
	onChange: (data: Partial<ReportFormData>) => void;
	screenOptions: Monitor[];
	errors?: Record<string, string>;
}

const RenameScreensSection: React.FC<RenameScreensSectionProps> = ({
	formData,
	onChange,
	screenOptions,
	errors,
}) => {
	const { t } = useTranslation();

	const handleAddRename = () => {
		const available = getAvailableScreens(
			screenOptions,
			formData.excluded_screen_IDs,
			formData.screen_renames,
			formData.screen_groups,
		);
		if (available.length === 0) return;

		const newEntry: ScreenGroup = {
			name: "",
			screen_ids: [available[0].Monitor_ID],
		};
		onChange({ screen_renames: [...formData.screen_renames, newEntry] });
	};

	const handleRemoveRename = (index: number) => {
		onChange({
			screen_renames: formData.screen_renames.filter((_, i) => i !== index),
		});
	};

	const handleScreenChange = (index: number, screenId: number) => {
		const updated = [...formData.screen_renames];
		updated[index] = { ...updated[index], screen_ids: [screenId] };
		onChange({ screen_renames: updated });
	};

	const handleNameChange = (index: number, name: string) => {
		const updated = [...formData.screen_renames];
		updated[index] = { ...updated[index], name };
		onChange({ screen_renames: updated });
	};

	const getAvailableForRow = (currentIndex: number): Monitor[] => {
		const otherRenames = formData.screen_renames.filter(
			(_, i) => i !== currentIndex,
		);
		return getAvailableScreens(
			screenOptions,
			formData.excluded_screen_IDs,
			otherRenames,
			formData.screen_groups,
		);
	};

	const hasAvailableScreens =
		getAvailableScreens(
			screenOptions,
			formData.excluded_screen_IDs,
			formData.screen_renames,
			formData.screen_groups,
		).length > 0;

	return (
		<div className="space-y-3">
			<div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
				<label className="block text-gray-700 text-sm font-bold">
					{t("form.rename.title")}
				</label>
				<Button
					type="button"
					size="sm"
					variant="secondary"
					onClick={handleAddRename}
					disabled={!hasAvailableScreens}
					fullWidth={false}
					className="w-full sm:w-auto"
				>
					{t("form.rename.addRename")}
				</Button>
			</div>

			{formData.screen_renames.length === 0 ? (
				<p className="text-sm text-gray-500">{t("form.rename.empty")}</p>
			) : (
				<div className="space-y-3">
					{formData.screen_renames.map((rename, index) => {
						const available = getAvailableForRow(index);
						const errorKey = `screen_renames_${index}`;

						return (
							<div
								key={index}
								className="p-4 border border-gray-200 rounded-xl bg-gradient-to-br from-gray-50 to-primary-50/30 shadow-sm hover:shadow-md transition-shadow"
							>
								<div className="flex items-center justify-between mb-3">
									<h3 className="font-semibold text-gray-800">
										{t("form.rename.renameTitle", { number: index + 1 })}
									</h3>
									<Button
										type="button"
										size="sm"
										variant="danger"
										onClick={() => handleRemoveRename(index)}
									>
										{t("common.remove")}
									</Button>
								</div>

								<div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
									{(() => {
										const currentId = rename.screen_ids[0];
										const currentScreen = screenOptions.find(
											(s) => s.Monitor_ID === currentId,
										);
										const isInAvailable = available.some(
											(s) => s.Monitor_ID === currentId,
										);
										const optionsList =
											currentScreen && !isInAvailable
												? [currentScreen, ...available]
												: available;
										return (
											<SearchableSelect<Monitor>
												label={t("form.rename.screen")}
												value={rename.screen_ids[0] ?? null}
												onChange={(id) => handleScreenChange(index, id)}
												options={optionsList}
												placeholder={t("form.rename.screenPlaceholder")}
											/>
										);
									})()}

									<Input
										label={t("form.rename.displayName")}
										value={rename.name}
										onChange={(e) => handleNameChange(index, e.target.value)}
										placeholder={t("form.rename.displayNamePlaceholder")}
										error={errors?.[errorKey]}
									/>
								</div>
							</div>
						);
					})}
				</div>
			)}

			{errors?.screen_renames && (
				<p className="text-sm text-red-600">{errors.screen_renames}</p>
			)}
		</div>
	);
};

export default RenameScreensSection;
